import React, { FC, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import Swal from 'sweetalert2'
import {
    Column,
    useFilters,
    useGlobalFilter,
    usePagination,
    useSortBy,
    useTable,
} from 'react-table'
import { KTIcon } from '../../../_payairo/helpers'
import clsx from 'clsx'
import { PageLink, PageTitle, useLayout } from '../../../_payairo/layout/core'

type WithdrawalType = {
    id: string           // Transaction ID
    name: string
    payairoTag: string
    type: string         // Crypto / Bank
    amount: number
    status: string       // Completed / Pending / Failed
    date: string         // ISO date
    method: string       // Bank Transfer / USDT / etc
    email: string
    tag: string
    createdAt: string
    completedAt?: string
}


const WithdrawHistoryTable: FC = () => {
    const [withdrawals, setWithdrawals] = useState<WithdrawalType[]>([])
    const [loading, setLoading] = useState(false)
    const [filterType, setFilterType] = useState('All')
    const [filterStatus, setFilterStatus] = useState('All')
    const { setUuidOfWithdraw } = useLayout()

    // ✅ Dummy Withdrawal Data
    useEffect(() => {
        setLoading(true)
        setTimeout(() => {
            setWithdrawals([

                {
                    id: 'WDL-001',
                    name: 'John Doe',
                    payairoTag: '@johnpay',
                    type: 'Crypto',
                    amount: 350.75,
                    status: 'Completed',
                    date: '2025-10-28T14:45:00',
                    method: 'USDT (TRC20)',
                    email: 'john@example.com',
                    tag: '@johnpay',
                    createdAt: '2025-10-28T14:45:00',
                    completedAt: '2025-10-28T15:00:00'
                },
                {
                    id: 'WDL-002',
                    name: 'Aarav Mehta',
                    payairoTag: '@aarav123',
                    type: 'Bank',
                    amount: 1200.0,
                    status: 'Pending',
                    date: '2025-10-27T09:20:00',
                    method: 'Bank Transfer',
                    email: 'aarav@example.com',
                    tag: '@aarav123',
                    createdAt: '2025-10-27T09:20:00'
                },
                {
                    id: 'WDL-003',
                    name: 'Sophia Patel',
                    payairoTag: '@sophiap',
                    type: 'Crypto',
                    amount: 480.99,
                    status: 'Failed',
                    date: '2025-10-25T18:10:00',
                    method: 'Bitcoin',
                    email: 'sophia@example.com',
                    tag: '@sophiap',
                    createdAt: '2025-10-25T18:10:00'
                }


            ])
            setLoading(false)
        }, 800)
    }, [])

    // ✅ Apply filters
    const filteredWithdrawals = useMemo(() => {
        return withdrawals.filter((wdl) => {
            const typeMatch = filterType === 'All' || wdl.type === filterType
            const statusMatch = filterStatus === 'All' || wdl.status === filterStatus
            return typeMatch && statusMatch
        })
    }, [withdrawals, filterType, filterStatus])

    // ✅ Define Columns
    const columns: Column<WithdrawalType>[] = useMemo(
        () => [
            {
                Header: 'S.No',
                id: 'serial-number',
                Cell: ({ row }) => <>{row.index + 1}</>,
            },
            {
                Header: 'Name',
                accessor: 'name',
            },
            {
                Header: 'PayAiro Tag',
                accessor: 'payairoTag',
            },
            {
                Header: 'Withdrawal Type',
                accessor: 'type',
                Cell: ({ value }) => (
                    <span
                        className={clsx('badge fs-7', {
                            'badge-light-primary': value === 'Crypto',
                            'badge-light-success': value === 'Bank',
                        })}
                    >
                        {value}
                    </span>
                ),
            },
            {
                Header: 'Amount',
                accessor: 'amount',
                Cell: ({ value }) => <span>${value.toFixed(2)}</span>,
            },
            {
                Header: 'Status',
                accessor: 'status',
                Cell: ({ value }) => (
                    <span
                        className={clsx('badge fs-7', {
                            'badge-light-success': value === 'Completed',
                            'badge-light-warning': value === 'Pending',
                            'badge-light-danger': value === 'Failed',
                        })}
                    >
                        {value}
                    </span>
                ),
            },
            {
                Header: 'Date & Time',
                accessor: 'date',
                Cell: ({ value }) => (
                    <div>
                        <div>{moment(value).format('DD MMM, YYYY')}</div>
                        <div className='text-muted fs-8'>{moment(value).format('hh:mm A')}</div>
                    </div>
                ),
            },
            {
                Header: 'Actions',
                Cell: ({ ...props }) => {
                    const uuid = props.row.original
                    return (

                        <a
                            id='withdraw-details-drawer-toggle'
                            href='javascript:void(0)'
                            className='btn h-35px w-65px btn-icon btn-light-info'
                            onClick={() => setUuidOfWithdraw(uuid)}
                        >
                            View
                        </a>
                    )
                },
            },
        ],
        []
    )

    // ✅ Table setup
    const table = useTable(
        { columns, data: filteredWithdrawals },
        useFilters,
        useGlobalFilter,
        useSortBy,
        usePagination
    )

    return (
        <div className='card'>
            <div className='card-header border-0 pt-5'>
                <h3 className='card-title align-items-start flex-column'>
                    <span className='card-label fw-bold fs-3 mb-1'>Withdraw History</span>
                    <span className='text-muted fw-semibold fs-7'>
                        Total {filteredWithdrawals.length} Withdrawals
                    </span>
                </h3>

                {/* Filters */}
                <div className='d-flex align-items-center gap-3'>
                    <select
                        className='form-select form-select-sm w-150px'
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                    >
                        <option value='All'>All Types</option>
                        <option value='Crypto'>Crypto</option>
                        <option value='Bank'>Bank</option>
                    </select>

                    <select
                        className='form-select form-select-sm w-150px'
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                    >
                        <option value='All'>All Status</option>
                        <option value='Completed'>Completed</option>
                        <option value='Pending'>Pending</option>
                        <option value='Failed'>Failed</option>
                    </select>

                    <input
                        type='text'
                        className='form-control form-control-sm w-200px'
                        placeholder='Search withdrawal'
                        value={table.state.globalFilter ?? ''}
                        onChange={(e) => table.setGlobalFilter(e.target.value)}
                    />
                </div>
            </div>

            <div className='card-body pt-3'>
                <div className='table-responsive'>
                    <table
                        {...table.getTableProps()}
                        className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'
                    >
                        <thead>
                            {table.headerGroups.map((headerGroup) => (
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map((column) => (
                                        <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                            {column.render('Header')}
                                            {column.isSorted ? (
                                                column.isSortedDesc ? (
                                                    <KTIcon iconName='down' className='ms-1 text-muted' />
                                                ) : (
                                                    <KTIcon iconName='up' className='ms-1 text-muted' />
                                                )
                                            ) : null}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody {...table.getTableBodyProps()}>
                            {loading ? (
                                <tr>
                                    <td colSpan={columns.length} className='text-center'>
                                        <div className='spinner-border text-primary' role='status'>
                                            <span className='visually-hidden'>Loading...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : table.page.length > 0 ? (
                                table.page.map((row) => {
                                    table.prepareRow(row)
                                    return (
                                        <tr {...row.getRowProps()}>
                                            {row.cells.map((cell) => (
                                                <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                                            ))}
                                        </tr>
                                    )
                                })
                            ) : (
                                <tr>
                                    <td colSpan={columns.length} className='text-center'>
                                        <span className='fw-bold'>No data found</span>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    {/* Pagination */}
                    <div className='d-flex justify-content-between align-items-center mt-5'>
                        <div>
                            <select
                                className='form-select form-select-sm form-select-solid'
                                value={table.state.pageSize}
                                onChange={(e) => table.setPageSize(Number(e.target.value))}
                            >
                                {[5, 10, 25, 50].map((size) => (
                                    <option key={size} value={size}>
                                        {size}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            Page <strong>{table.state.pageIndex + 1}</strong> of {table.pageOptions.length}
                        </div>
                        <div>
                            <button
                                className='btn btn-sm'
                                onClick={() => table.previousPage()}
                                disabled={!table.canPreviousPage}
                            >
                                <KTIcon iconName='left' className='text-gray fs-1 me-5' />
                            </button>
                            <button
                                className='btn btn-sm'
                                onClick={() => table.nextPage()}
                                disabled={!table.canNextPage}
                            >
                                <KTIcon iconName='right' className='text-gray fs-1 me-5' />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default WithdrawHistoryTable

const myBreadCrumbs: PageLink[] = [
    { title: 'Home', path: '/dashboard', isSeparator: false, isActive: false },
    { title: '', path: '', isSeparator: true, isActive: false },
]

const WithdrawHistoryWrapper: FC = () => {
    const { setRightTolbar } = useLayout()
    useEffect(() => {
        setRightTolbar('')
    }, [])
    return (
        <>
            <PageTitle breadcrumbs={myBreadCrumbs}>Withdraw History</PageTitle>
            <WithdrawHistoryTable />
        </>
    )
}

export { WithdrawHistoryWrapper }
